import { defineStore } from 'pinia';
import useApi from '@/composeables/useApi';
import {
  Workplace,
  Incident,
  IncidentField,
  IncidentRepeaterRow,
  IncidentRepeaterField,
  RepeaterSubField,
  Template,
  IncidentUser,
  ExternalEmail,
  AdditionalEmail
} from '@/constants/types/api';
import { FieldTypes } from '@/constants/field-types';
import { alertController } from '@ionic/vue';
import IncidentStatus from '@/constants/incident-status';
import { simpleAlert } from '@/utils/alert';
import { useUserStore } from '@/store/user';
import { useMandatoryIncidentDates } from '@/composeables/useMandatoryIncidentDates';
import { emailValid, isNumericValue } from '@/utils/validation';

export const isRepeaterField = (field: IncidentField): field is IncidentRepeaterField => {
  return field.type === FieldTypes.REPEATER;
};

export const hasRepeaterStructure = (field: IncidentField): field is IncidentRepeaterField => {
  return isRepeaterField(field) && Array.isArray(field.sub_fields) && Array.isArray(field.rows);
};

const LOCAL_REPEATER_ROW_KEY = '__local_repeater_row_key';

const createLocalRepeaterRowKey = (): string => {
  return `local-row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
};

const getTrimmedValue = (value: string | null | undefined): string => {
  return typeof value === 'string' ? value.trim() : '';
};

const hasScalarValue = (value: unknown): boolean => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  return Boolean(value);
};

const toBooleanValue = (value: unknown, fallback = false): boolean => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value !== 0;
  }

  if (typeof value === 'string') {
    const normalizedValue = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalizedValue)) {
      return true;
    }
    if (['false', '0', 'no', 'off', ''].includes(normalizedValue)) {
      return false;
    }
  }

  return fallback;
};

const normalizeRepeaterRowTitle = (value: string | null | undefined): string | null => {
  const trimmedValue = getTrimmedValue(value);
  return trimmedValue.length > 0 ? trimmedValue : null;
};

const normalizeRepeaterSubFields = (subFields: RepeaterSubField[] | undefined): RepeaterSubField[] => {
  const safeSubFields = Array.isArray(subFields) ? subFields : [];
  return [...safeSubFields].sort((left, right) => left.order - right.order);
};

const normalizeRepeaterRow = (row: IncidentRepeaterRow | null | undefined): IncidentRepeaterRow => {
  const safeRow = row || {};
  const normalizedRow: IncidentRepeaterRow = Object.entries(safeRow)
    .reduce((acc, [key, value]) => {
      if (typeof value === 'string' || value === null || value === undefined) {
        acc[key] = value;
      }
      return acc;
    }, {} as IncidentRepeaterRow);
  const repeaterRowKey = getTrimmedValue(normalizedRow.repeater_row_key);
  normalizedRow[LOCAL_REPEATER_ROW_KEY] = repeaterRowKey || createLocalRepeaterRowKey();
  if (repeaterRowKey) {
    normalizedRow.repeater_row_key = repeaterRowKey;
  } else {
    delete normalizedRow.repeater_row_key;
  }

  return normalizedRow;
};

const normalizeRepeaterField = (field: IncidentField): IncidentRepeaterField => {
  const normalizedSubFields = normalizeRepeaterSubFields(field.sub_fields);
  const safeRows = Array.isArray(field.rows) ? field.rows : [];

  return {
    ...field,
    repeater_row_title: normalizeRepeaterRowTitle(field.repeater_row_title),
    rows: safeRows.map(row => normalizeRepeaterRow(row)),
    sub_fields: normalizedSubFields,
    type: FieldTypes.REPEATER,
  };
};

const normalizeIncidentForStore = (incident: Incident): Incident => {
  return {
    ...incident,
    incident_sections: incident.incident_sections.map(section => ({
      ...section,
      incident_fields: section.incident_fields.map(field => {
        return isRepeaterField(field) ? normalizeRepeaterField(field) : field;
      }),
    })),
  };
};

const sanitizeRepeaterRowForPayload = (row: IncidentRepeaterRow): IncidentRepeaterRow => {
  const { [LOCAL_REPEATER_ROW_KEY]: ignoredLocalRowKey, ...safeRow } = row;
  void ignoredLocalRowKey;
  return Object.entries(safeRow).reduce((acc, [key, value]) => {
    if (typeof value === 'string' || value === null) {
      acc[key] = value;
    }

    return acc;
  }, {} as IncidentRepeaterRow);
};

export const sanitizeIncidentForPayload = (incident: Incident): Incident => {
  return {
    ...incident,
    send_to_warningslist: toBooleanValue(incident.send_to_warningslist, false),
    incident_sections: incident.incident_sections.map(section => ({
      ...section,
      incident_fields: section.incident_fields.map(field => {
        if (isRepeaterField(field)) {
          const normalizedRepeaterField = normalizeRepeaterField(field);
          return {
            ...normalizedRepeaterField,
            rows: normalizedRepeaterField.rows.map(row => sanitizeRepeaterRowForPayload(row)),
            sub_fields: normalizeRepeaterSubFields(normalizedRepeaterField.sub_fields),
          };
        }

        const scalarField = { ...field };
        delete scalarField.repeater_row_title;
        delete scalarField.rows;
        delete scalarField.sub_fields;
        return scalarField;
      }),
    })),
  };
};

const hasRepeaterRowAnswer = (row: IncidentRepeaterRow, subFields: RepeaterSubField[]): boolean => {
  if (subFields.length) {
    return subFields.some(subField => getTrimmedValue(row[subField.label]).length > 0);
  }

  return Object.entries(row)
    .filter(([key]) => ![LOCAL_REPEATER_ROW_KEY, 'repeater_row_key'].includes(key))
    .some(([, value]) => getTrimmedValue(value).length > 0);
};

const hasFieldAnswer = (field: IncidentField): boolean => {
  if (isRepeaterField(field)) {
    const normalizedRepeaterField = normalizeRepeaterField(field);
    return normalizedRepeaterField.rows.some(row => hasRepeaterRowAnswer(row, normalizedRepeaterField.sub_fields));
  }

  if (field.allow_additional_value && !hasScalarValue(field.value)) {
    return hasScalarValue(field.additional_value);
  }

  return hasScalarValue(field.value);
};

const isFieldInvalid = (field: IncidentField, requireMandatoryOnly = true): boolean => {
  if (isRepeaterField(field)) {
    const normalizedRepeaterField = normalizeRepeaterField(field);
    const hasRows = normalizedRepeaterField.rows.length > 0;

    if (requireMandatoryOnly && !field.mandatory && !hasRows) {
      return false;
    }

    if (field.mandatory && normalizedRepeaterField.rows.length === 0) {
      return true;
    }

    return normalizedRepeaterField.rows.some(row => {
      return normalizedRepeaterField.sub_fields.some(subField => {
        const value = getTrimmedValue(row[subField.label]);
        if (subField.mandatory && value.length === 0) {
          return true;
        }

        if (subField.numeric && value.length > 0 && !isNumericValue(value)) {
          return true;
        }

        return false;
      });
    });
  }

  // Checked before the mandatory-only shortcut so optional numeric fields are validated too
  if (field.type === FieldTypes.TEXT && field.numeric && hasScalarValue(field.value) && !isNumericValue(String(field.value))) {
    return true;
  }

  if (requireMandatoryOnly && !field.mandatory) {
    return false;
  }

  if (field.allow_additional_value && !hasScalarValue(field.value)) {
    return !hasScalarValue(field.additional_value);
  }

  return !hasScalarValue(field.value);
};

export const useIncidentStore = defineStore('incidentStore', {
  state: () => ({
    incidentTemplates: [] as Template[],
    incidentData: null as null | Incident,
    incidentLoading: false,
    externalEmails: [] as ExternalEmail[],
    step: 0,
    formValidated: false,
    userValidated: false,
    additionalEmailsValidated: false,
    myIncidents: [] as Incident[],
    myIncidentsNextPage: null as number | null,
    myDrafts: [] as Incident[],
    myDraftsNextPage: null as number | null,
    myIncidentSearchQuery: '',
    shouldFilterMyIncidents: true,
    groupIds: [],
    incidentModalOpen: false,
    editingCompletedIncident: false,
    completedIncidentSnapshot: null as Incident | null,
    pendingIncidentId: null as number | null,
    openHistoryTab: false,
    backShouldCloseIncident: false,
  }),
  actions: {
    onNext() {
      this.step = this.step + 1;
    },
    async onBack() {
      if (this.backShouldCloseIncident) {
        this.closeIncidentModal();
        return;
      }
      if (this.step - 1 === 0 && (this.incident && this.incident.status !== IncidentStatus.Completed)) {
        await this.leaveIncidentReport(0);
      } else {
        this.step = this.step - 1;
        if (this.step === 0) {
          await this.cancelIncident();
        }
      }
    },
    onSet(step: number) {
      this.step = step;
    },
    async fetchTemplates(fromCache = true) {
      const { apiGet } = useApi();
      const templateRes = await apiGet<Template[]>('/incidenttemplates', null, fromCache);
      if (templateRes) {
        this.incidentTemplates = templateRes;
      }
      const externalEmailsRes = await apiGet<any>('/incidents/externalemails');
      if (externalEmailsRes) {
        this.externalEmails = externalEmailsRes;
      }
    },
    async fetchIncident({ templateId, incidentId }: { templateId?: number, incidentId?: number }): Promise<boolean> {
      const { apiGet } = useApi();
      this.incidentLoading = true;
      this.editingCompletedIncident = false;
      this.completedIncidentSnapshot = null;
      this.incidentData = null;
      this.openHistoryTab = false;
      if (templateId) {
        const res = await apiGet<Incident>(`/incidents/temporary/${templateId}`, null, false);
        if (res) {
          this.incidentData = normalizeIncidentForStore({
            ...res,
            send_to_warningslist: toBooleanValue(res.send_to_warningslist, false),
            user: res.user || {
              first_name: '',
              last_name: '',
              email: '',
              phone: '',
              company: {
                name: '',
                org_nr: '',
                sector: null,
              },
              workplace: {
                address: '',
                city: '',
                id: '',
                name: '',
                zip: '',
              },
            },
          });
        }
        this.incidentLoading = false;
        return !!this.incidentData;
      } else if (incidentId) {
        const res = await apiGet<Incident>(`/incidents/${incidentId}`);
        if (res) {
          this.incidentData = normalizeIncidentForStore({
            ...res,
            user: res.user || {
              first_name: '',
              last_name: '',
              email: '',
              phone: '',
              company: {
                name: '',
                org_nr: '',
                sector: null,
              },
              workplace: {
                address: '',
                city: '',
                id: '',
                name: '',
                zip: '',
              },
            },
          });
        }
        this.incidentLoading = false;
        return !!this.incidentData;
      }
      this.incidentLoading = false;
      return false;
    },
    startEditingCompletedIncident() {
      if (this.incident?.status === IncidentStatus.Completed) {
        this.editingCompletedIncident = true;
        this.completedIncidentSnapshot = JSON.parse(JSON.stringify(this.incident)) as Incident;
      }
    },
    stopEditingCompletedIncident() {
      this.editingCompletedIncident = false;
      this.completedIncidentSnapshot = null;
    },
    discardEditingCompletedIncident() {
      if (this.completedIncidentSnapshot) {
        this.incidentData = JSON.parse(JSON.stringify(this.completedIncidentSnapshot)) as Incident;
      }
      this.editingCompletedIncident = false;
      this.completedIncidentSnapshot = null;
      this.step = 1;
    },
    setUserField(field: 'email' | 'first_name' | 'last_name' | 'phone', input: string) {
      if (this.incidentData) {
        this.incidentData.user[field] = input;
      }
    },
    setField(field: 'date_start' | 'date_end' | 'police_email_id' | 'insurance_email_id' | 'status', input: any) {
      if (this.incidentData) {
        this.incidentData[field] = input;

        if (field === 'police_email_id') {
          this.incidentData.send_to_police = !!input;
        }
      }
    },
    setCompany({ name, orgNr }: { name: string; orgNr: string }) {
      if (this.incidentData) {
        this.incidentData.user.company = {
          name,
          org_nr: orgNr,
          sector: null,
        };
      }
    },
    setSector(sectorId: number) {
      if (this.incidentData) {
        this.incidentData.user.company.sector = sectorId;
      }
    },
    setOtherLocation({ key, input }: { key: 'location_address' | 'location_zip' | 'location_city'; input: string}) {
      if (this.incidentData) {
        this.incidentData[key] = input;
      }
    },
    setLocation(input: Workplace) {
      if (this.incidentData) {
        this.incidentData.user.workplace = {
          name: input.Namn,
          zip: input.Postnummer,
          city: input.Postort,
          address: input.Gatuadress,
          id: input.ArbetsstalleID,
        };
      }
    },
    setAdditionalEmails(additionalEmails: AdditionalEmail[]) {
      if (this.incidentData) {
        this.incidentData.additional_emails = additionalEmails
          .filter(additionalEmail => additionalEmail.email)
          .map(additionalEmail => ({
            id: additionalEmail.id,
            email: additionalEmail.email,
          }));
      }
    },
    ensureRepeaterFieldShape(field: IncidentField): IncidentRepeaterField | null {
      if (!isRepeaterField(field)) {
        return null;
      }

      const normalizedRepeaterField = normalizeRepeaterField(field);
      field.sub_fields = normalizedRepeaterField.sub_fields;
      field.rows = normalizedRepeaterField.rows;

      return field as IncidentRepeaterField;
    },
    addRepeaterRow(field: IncidentField) {
      const repeaterField = this.ensureRepeaterFieldShape(field);
      if (!repeaterField) {
        return;
      }

      const newRow: IncidentRepeaterRow = {
        [LOCAL_REPEATER_ROW_KEY]: createLocalRepeaterRowKey(),
      };
      repeaterField.sub_fields.forEach(subField => {
        newRow[subField.label] = null;
      });
      repeaterField.rows = [...repeaterField.rows, newRow];
    },
    removeRepeaterRow(field: IncidentField, rowIndex: number) {
      const repeaterField = this.ensureRepeaterFieldShape(field);
      if (!repeaterField) {
        return;
      }

      if (rowIndex < 0 || rowIndex >= repeaterField.rows.length) {
        return;
      }

      repeaterField.rows = repeaterField.rows.filter((_, index) => index !== rowIndex);
    },
    setRepeaterRowValue(field: IncidentField, rowIndex: number, subFieldLabel: string, value: string | null) {
      const repeaterField = this.ensureRepeaterFieldShape(field);
      if (!repeaterField) {
        return;
      }

      if (!repeaterField.rows[rowIndex]) {
        return;
      }

      repeaterField.rows[rowIndex][subFieldLabel] = value;
    },
    hasFieldAnswer(field: IncidentField): boolean {
      return hasFieldAnswer(field);
    },
    isFieldInvalid(field: IncidentField, requireMandatoryOnly = true): boolean {
      return isFieldInvalid(field, requireMandatoryOnly);
    },
    async saveDraft(step: number | null = null) {
      this.userValidated = false;
      this.additionalEmailsValidated = false;
      const userPassed = await this.validateUserFields();
      const additionalEmailsPassed = this.validateAdditionalEmails();

      if (!userPassed) {
        const userStep = this.hasOptionalFields ? 3 : 2;
        await this.promptValidateError(userStep);
      } else if (!additionalEmailsPassed) {
        const userStep = this.hasOptionalFields ? 4 : 3;
        await this.promptValidateError(userStep);
      }

      if (userPassed && this.incidentData && additionalEmailsPassed) {
        await this.setField('status', IncidentStatus.Draft);
        await this.saveIncident(step);
      }
    },
    async completeIncident(step: number | null = null) {
      this.formValidated = false;
      this.additionalEmailsValidated = false;
      this.userValidated = false;

      const obligatoryFormPassed = await this.validateFormFields();
      const nonObligatoryFormPassed = this.hasOptionalFields ? await this.validateOptionalFormFields() : true;
      const userPassed = await this.validateUserFields();
      const additionalEmailsPassed = this.validateAdditionalEmails();

      if (!obligatoryFormPassed) {
        await this.promptValidateError(1);
      } else if (!nonObligatoryFormPassed) {
        await this.promptValidateError(2);
      } else if (!userPassed) {
        const userStep = this.hasOptionalFields ? 3 : 2;
        await this.promptValidateError(userStep);
      } else if (!additionalEmailsPassed) {
        const userStep = this.hasOptionalFields ? 4 : 3;
        await this.promptValidateError(userStep);
      }

      if (obligatoryFormPassed && nonObligatoryFormPassed && userPassed && this.incidentData && additionalEmailsPassed) {
        await this.setField('status', IncidentStatus.Completed);
        await this.saveIncident(step);
      }
    },
    async saveIncident(step: number | null = null) {
      const { apiPost, apiPut, handleApiError } = useApi();
      const userStore = useUserStore();
      const isNew = !this.incident.id;
      const payload = sanitizeIncidentForPayload(this.incident);
      this.incidentLoading = true;
      let res;
      if (isNew) {
        res = await apiPost('/incidents', payload);
      } else {
        res = await apiPut('/incidents/' + this.incident.id, payload);
      }
      this.incidentLoading = false;
      if (res) {
        if (this.incident.status === IncidentStatus.Completed) {
          if (this.groupIds.length) {
            await apiPost(`/groups/incident/${res.id}/share`, { groups: this.groupIds });
          }
          if (step === null) {
            this.closeIncidentModal();
          }
          if (this.editingCompletedIncident) {
            await simpleAlert('Din anmälan är nu uppdaterad');
          }
          if (step !== 4) {
            await simpleAlert('Tack för din anmälan', userStore.currentUser ? 'Nu kan du se händelsen under Mina anmälningar inne i anmälningsflödet.' : undefined);
          }
        } else if (this.incident.status === IncidentStatus.Draft) {
          if (step === null) {
            this.closeIncidentModal();
          }
          await simpleAlert('Ditt utkast är sparat', 'Du kan ändra och skicka in händelsen under Mina anmälningar inne i anmälningsflödet.');
        }

        this.step = this.editingCompletedIncident ? 1 : step;
        await this.fetchUserIncidents();
        this.editingCompletedIncident = false;
        this.completedIncidentSnapshot = null;
        if (step !== 4) {
          this.incidentData = null;
        }
      } else {
        await handleApiError();
        await this.setField('status', null);
        this.step = this.hasOptionalFields ? 3 : 2;
      }
    },
    async deleteIncident() {
      const { apiDelete, handleApiError } = useApi();

      const res = await apiDelete(`/incidents/${this.incident.id}`);

      if (!res) {
        await handleApiError();
      } else {
        await this.fetchUserIncidents();
        this.step = 0;
        this.incidentData = null;
      }
    },
    async validateFormFields() {
      if (this.obligatoryFields.some(field => this.isFieldInvalid(field, true))) {
        this.formValidated = true;
        return false;
      }

      return true;
    },
    async validateOptionalFormFields() {
      if (this.optionalFields.some(field => this.isFieldInvalid(field, true))) {
        this.formValidated = true;
        return false;
      }

      return true;
    },
    async validateUserFields() {
      const invalidUserFields = Object.entries(this.incident.user).filter(field => !field[1]);
      const invalidCompanyFields = Object.entries(this.incident.user.company).filter(field => !['sector'].includes(field[0]) && !field[1]);
      const invalidWorkplaceFields = Object.entries(this.incident.user.workplace).filter(field => !['id'].includes(field[0]) && !field[1]);

      if (invalidUserFields.length > 0 || invalidCompanyFields.length > 0 || invalidWorkplaceFields.length > 0 || !this.validateIncidentDates()) {
        this.userValidated = true;
        return false;
      }

      return true;
    },
    /**
     * The dates live on the same step as the user fields, so they share the
     * `userValidated` flag. When the app customization does not make them
     * mandatory they are prefilled with "now" and can never be empty.
     */
    validateIncidentDates() {
      const { datesMandatory } = useMandatoryIncidentDates();

      if (!datesMandatory.value) {
        return true;
      }

      return !!this.incident.date_start && !!this.incident.date_end;
    },
    validateAdditionalEmails() {
      const additionalEmails = this.incident.additional_emails;
      const hasInvalidEmail = additionalEmails
        .map(additionalEmail => !!emailValid(additionalEmail.email))
        .some(item => !item);
      this.additionalEmailsValidated = true;
      return !hasInvalidEmail;
    },
    async promptValidateError(step) {
      const alert = await alertController.create({
        header: 'Fyll i alla fält',
        subHeader: 'Rätta fälten som är rödmarkerade',
        buttons: [{
          text: 'OK',
          role: 'confirm',
          handler: () => {
            setTimeout(() => this.onSet(step), 200);
          },
        }],

      });

      await alert.present();
    },

    async leaveIncidentReport(step: number | null = null) {
      const alert = await alertController.create({
        header: 'Vill du avbryta anmälan?',
        message: 'Om du avbryter anmälan förlorar du alla ändringar du gjort.',
        buttons: [
          {
            text: 'Släng',
            role: 'destructive',
            handler: async() => {
              const incidentId = this.incident?.id;
              const isSavedDraft = incidentId && this.myDrafts.some(d => d.id === incidentId);
              if (isSavedDraft) {
                const { apiDelete } = useApi();
                await apiDelete(`/incidents/${incidentId}`);
                await this.fetchUserIncidents();
              }
              await this.cancelIncident();
              if (step === null) {
                this.closeIncidentModal();
              }
            },
          },
          {
            text: 'Spara utkast',
            role: 'confirm',
            handler: async() => {
              await this.saveDraft(step);
            },
          },
          {
            text: 'Avbryt',
            role: 'cancel',
          },
        ],
      });

      await alert.present();
    },
    cancelIncident() {
      this.incidentData = null;
      this.incidentLoading = false;
      this.step = 0;
      this.formValidated = false;
      this.userValidated = false;
      this.additionalEmailsValidated = false;
      this.editingCompletedIncident = false;
      this.completedIncidentSnapshot = null;
      this.pendingIncidentId = null;
      this.openHistoryTab = false;
      this.backShouldCloseIncident = false;
    },
    async fetchMyIncidents(toNextPage?: boolean) {
      this.incidentLoading = true;
      const { apiGet } = useApi();
      const apiPage = toNextPage && this.myIncidentsNextPage ? this.myIncidentsNextPage : 1;
      let endpoint = `/v2/users/me/incidents?status=${IncidentStatus.Completed}&page=${apiPage}`;

      if (this.myIncidentSearchQuery) {
        endpoint += `&query=${this.myIncidentSearchQuery}`;
      }

      if (this.shouldFilterMyIncidents) {
        endpoint += `&only_mine=${this.shouldFilterMyIncidents}`;
      }

      const myIncidentsRes = await apiGet<{
        data: Incident[];
        next_page_url: string;
        current_page: number;
      }>(endpoint);
      if (myIncidentsRes) {
        this.myIncidentsNextPage = myIncidentsRes.next_page_url ? myIncidentsRes.current_page + 1 : null;
        this.myIncidents = toNextPage ? [...this.myIncidents, ...myIncidentsRes.data] : myIncidentsRes.data || [];
        this.incidentLoading = false;
      }
    },
    async fetchMyDrafts(toNextPage?: boolean) {
      this.incidentLoading = true;
      const { apiGet } = useApi();
      const apiPage = toNextPage && this.myDraftsNextPage ? this.myDraftsNextPage : 1;

      let endpoint = `/v2/users/me/incidents?status=${IncidentStatus.Draft}&page=${apiPage}`;

      if (this.myIncidentSearchQuery) {
        endpoint += `&query=${this.myIncidentSearchQuery}`;
      }

      if (this.shouldFilterMyIncidents) {
        endpoint += `&only_mine=${this.shouldFilterMyIncidents}`;
      }

      const myDraftsRes = await apiGet<{
        data: Incident[];
        next_page_url: string;
        current_page: number;
      }>(endpoint);
      if (myDraftsRes) {
        this.myDraftsNextPage = myDraftsRes.next_page_url ? myDraftsRes.current_page + 1 : null;
        this.myDrafts = toNextPage ? [...this.myDrafts, ...myDraftsRes.data] : myDraftsRes.data || [];
        this.incidentLoading = false;
      }
    },
    async fetchUserIncidents() {
      const userStore = useUserStore();
      if (userStore.currentUser) {
        await this.fetchMyIncidents();
        await this.fetchMyDrafts();
      }
    },
    async fetchMoreDrafts() {
      await this.fetchMyDrafts(true);
    },
    async fetchMoreIncidents() {
      await this.fetchMyIncidents(true);
    },
    async onMyIncidentSearch(input: string) {
      this.myIncidentSearchQuery = input;
      await this.fetchUserIncidents();
    },
    async filterMyIncidents(filter: boolean) {
      this.shouldFilterMyIncidents = filter;
      await this.fetchUserIncidents();
    },
    setGroupIds(ids: Array<number | string>) {
      this.groupIds = ids;
    },
    setOpenHistoryTab(value: boolean) {
      this.openHistoryTab = value;
    },
    setPendingIncidentId(id: number | null) {
      this.pendingIncidentId = id;
    },
    async openIncidentModal(templateId?, incidentId?: number) {
      this.incidentModalOpen = true;
      this.editingCompletedIncident = false;
      this.completedIncidentSnapshot = null;
      this.pendingIncidentId = incidentId || null;
      this.openHistoryTab = false;
      this.backShouldCloseIncident = !!incidentId;
      this.step = 0;
      this.formValidated = false;
      this.userValidated = false;
      this.additionalEmailsValidated = false;
      if (templateId) {
        await this.fetchIncident({ templateId });
        this.onNext();
        this.pendingIncidentId = null;
        this.backShouldCloseIncident = false;
      }
    },
    closeIncidentModal() {
      this.cancelIncident();
      this.incidentModalOpen = false;
    },
  },
  getters: {
    isIncidentModalOpen(): boolean {
      return this.incidentModalOpen;
    },
    templates(): Template[] {
      return this.incidentTemplates;
    },
    incident(): Incident {
      return this.incidentData as Incident;
    },
    obligatoryFields(): IncidentField[] {
      return this.incident?.incident_sections[0]?.incident_fields || [];
    },
    optionalFields(): IncidentField[] {
      return this.incident?.incident_sections[1]?.incident_fields || [];
    },
    obligatoryFieldsLabels(): { label: string, subLabel: string } {
      return {
        label: this.incident?.incident_sections[0]?.label,
        subLabel: this.incident?.incident_sections[0]?.sublabel,
      };
    },
    optionalFieldsLabels(): { label: string, subLabel: string } {
      return {
        label: this.incident?.incident_sections[1]?.label,
        subLabel: this.incident?.incident_sections[1]?.sublabel,
      };
    },
    hasOptionalFields(): boolean {
      return this.optionalFields.length > 0;
    },
    policeEmails(): ExternalEmail[] {
      return this.externalEmails.filter(email => email.type === 0);
    },
    insuranceEmails(): ExternalEmail[] {
      return this.externalEmails.filter(email => email.type === 1);
    },
    user(): IncidentUser {
      return this.incident?.user || {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        company: {
          name: '',
          org_nr: '',
          sector: null,
        },
        workplace: {
          address: '',
          city: '',
          id: '',
          name: '',
          zip: '',
        },
      };
    },
    location(): { location_address: string; location_zip: string; location_city: string } {
      return {
        location_address: this.incident?.location_address || '',
        location_zip: this.incident?.location_zip || '',
        location_city: this.incident?.location_city || '',
      };
    },
  },
});
